import { useState, useEffect, useCallback } from 'react';
import type { ModuleCode } from '../api/types';

// =============================================================================
// Slice 7.9: AssetGrid view state — collapsible column groups + visibility +
// view presets, persisted to localStorage.
//
// Why a dedicated module:
//   - Keeps the AssetGrid component focused on rendering, not state plumbing
//   - The hook is independently testable (pure-ish, just localStorage I/O)
//   - The group definitions are shared between the Columns dropdown UI and the
//     visibility logic — defining them once here avoids drift
//
// State shape stored in localStorage:
//   {
//     version: 1,
//     collapsedGroups: { foundation: false, advanced: true, ... },
//     hiddenColumns: { 'business_unit_name': false, ... },
//     view: 'standard' | 'compact' | 'detailed'
//   }
//
// We version the schema so future iterations can migrate or reset cleanly.
// =============================================================================

// Slice 8: bumped to v2 because Cryptography joined the Advanced group,
// changing the default "collapsed by default" calculus. Old v1 entries
// are silently discarded by loadPersistedState() and the user falls back
// to defaults — acceptable, this is only column visibility preferences.
export const STORAGE_KEY = 'qram.assetgrid.v3';

export type ViewPreset = 'standard' | 'compact' | 'detailed';

/**
 * Logical column groups in the AssetGrid. The IDs match the group header IDs
 * declared in asset-grid.tsx (`group_foundation`, etc.) so that toggling a
 * group here directly drives column visibility there.
 *
 * `alwaysVisible: true` means the group cannot be collapsed (Identity is
 * always visible because it carries the asset name; Composite carries the
 * threat-selection dot and worst-overall-risk).
 */
export interface ColumnGroupDef {
  id: string;
  label: string;
  alwaysVisible: boolean;
  /**
   * Module codes whose columns belong to this group. The integrated module of
   * the group (if any) shows as the summary score when collapsed.
   */
  modules: ModuleCode[];
  integratedModule?: ModuleCode;
}

export const COLUMN_GROUPS: ColumnGroupDef[] = [
  {
    id: 'group_identity',
    label: 'Identity',
    alwaysVisible: true,
    modules: [],
  },
  {
    id: 'group_properties',
    label: 'Properties',
    // Slice 7.9: Properties has no integrated/summary module, so we don't
    // make it collapsible — collapsing would leave an empty group header.
    // Individual property columns can still be hidden via the Columns
    // dropdown.
    alwaysVisible: true,
    modules: [],
  },
  {
    id: 'group_foundation',
    label: 'Baseline risk (business exposure)',
    alwaysVisible: false,
    modules: ['timeline', 'data_risk', 'supply_chain', 'migration_feasibility'],
    integratedModule: 'integrated_foundation',
  },
  {
    id: 'group_advanced',
    label: 'Technical exposure and Overall risk',
    alwaysVisible: false,
    // Slice 8: Threat + Cryptography (Compliance arrives in Slice 9).
    // integrated_advanced summarises the group when collapsed.
    modules: ['threat', 'cryptography'],
    integratedModule: 'integrated_advanced',
  },
];

interface PersistedState {
  version: 2;
  collapsedGroups: Record<string, boolean>;
  hiddenColumns: Record<string, boolean>;
  view: ViewPreset;
}

const DEFAULT_STATE: PersistedState = {
  version: 2,
  collapsedGroups: {
    group_foundation: false,
    group_advanced: false,  // Slice 8: Advanced now has Threat + Cryptography, worth showing
  },
  hiddenColumns: {},
  view: 'standard',
};

function loadPersistedState(): PersistedState {
  if (typeof window === 'undefined') return DEFAULT_STATE;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_STATE;
    const parsed = JSON.parse(raw) as Partial<PersistedState>;
    if (parsed.version !== 2) return DEFAULT_STATE;
    return {
      version: 2,
      collapsedGroups: { ...DEFAULT_STATE.collapsedGroups, ...(parsed.collapsedGroups ?? {}) },
      hiddenColumns: { ...DEFAULT_STATE.hiddenColumns, ...(parsed.hiddenColumns ?? {}) },
      view: (parsed.view as ViewPreset) ?? DEFAULT_STATE.view,
    };
  } catch {
    return DEFAULT_STATE;
  }
}

function savePersistedState(state: PersistedState): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Storage full or disabled — silently ignore. The grid still works
    // with in-memory state for the session.
  }
}

/**
 * Apply a view preset to the persisted state. Compact = collapse all
 * collapsible groups. Detailed = expand all. Standard = restore defaults.
 *
 * Exported pure function so tests can verify the preset semantics without
 * rendering the component.
 */
export function applyViewPreset(state: PersistedState, view: ViewPreset): PersistedState {
  const next: PersistedState = { ...state, view };
  if (view === 'compact') {
    next.collapsedGroups = {};
    for (const g of COLUMN_GROUPS) {
      if (!g.alwaysVisible) next.collapsedGroups[g.id] = true;
    }
  } else if (view === 'detailed') {
    next.collapsedGroups = {};
    for (const g of COLUMN_GROUPS) {
      next.collapsedGroups[g.id] = false;
    }
  } else {
    // standard: restore defaults
    next.collapsedGroups = { ...DEFAULT_STATE.collapsedGroups };
  }
  return next;
}

/**
 * Decide whether a leaf column should be hidden, given the current view state.
 *
 * Hidden when EITHER:
 *   - The user explicitly hid it via the Columns dropdown
 *   - Its enclosing group is collapsed AND it is not the group's summary column
 *
 * Exported pure function so tests can verify visibility logic without
 * rendering.
 */
export function isColumnHidden(args: {
  columnId: string;
  groupId: string | null;
  isGroupSummary: boolean;
  hiddenColumns: Record<string, boolean>;
  collapsedGroups: Record<string, boolean>;
}): boolean {
  if (args.hiddenColumns[args.columnId]) return true;
  if (args.groupId && args.collapsedGroups[args.groupId]) {
    // When a group is collapsed, only its summary column is visible.
    return !args.isGroupSummary;
  }
  return false;
}

/**
 * The hook the AssetGrid consumes. Hydrates from localStorage on mount,
 * persists any change.
 */
export function useGridViewState() {
  const [state, setState] = useState<PersistedState>(() => loadPersistedState());

  useEffect(() => {
    savePersistedState(state);
  }, [state]);

  const toggleGroup = useCallback((groupId: string) => {
    setState((s) => ({
      ...s,
      collapsedGroups: {
        ...s.collapsedGroups,
        [groupId]: !s.collapsedGroups[groupId],
      },
    }));
  }, []);

  const setColumnVisibility = useCallback((columnId: string, visible: boolean) => {
    setState((s) => ({
      ...s,
      hiddenColumns: {
        ...s.hiddenColumns,
        [columnId]: !visible,
      },
    }));
  }, []);

  const setView = useCallback((view: ViewPreset) => {
    setState((s) => applyViewPreset(s, view));
  }, []);

  const resetToDefaults = useCallback(() => {
    setState(DEFAULT_STATE);
  }, []);

  return {
    collapsedGroups: state.collapsedGroups,
    hiddenColumns: state.hiddenColumns,
    view: state.view,
    toggleGroup,
    setColumnVisibility,
    setView,
    resetToDefaults,
  };
}
